import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { translations, languages, currencies } from '../i18n/translations';

const LanguageContext = createContext(null);

const detectBrowserLanguage = () => {
  const browserLang = navigator.language.split('-')[0];
  const supportedLang = languages.find(l => l.code === browserLang);
  return supportedLang ? browserLang : 'en';
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('wedvaya_language') || detectBrowserLanguage();
  });
  
  const [currency, setCurrency] = useState(() => {
    return localStorage.getItem('wedvaya_currency') || 'EUR';
  });

  const [isRTL, setIsRTL] = useState(false);

  useEffect(() => {
    localStorage.setItem('wedvaya_language', language);
    const langConfig = languages.find(l => l.code === language);
    setIsRTL(langConfig?.rtl || false);
    document.documentElement.dir = langConfig?.rtl ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
  }, [language]);

  useEffect(() => {
    localStorage.setItem('wedvaya_currency', currency);
  }, [currency]);

  const t = useCallback((key) => {
    const keys = key.split('.');
    let value = translations[language];
    
    for (const k of keys) {
      if (value && typeof value === 'object') {
        value = value[k];
      } else {
        // Fallback to English
        value = translations['en'];
        for (const fallbackKey of keys) {
          if (value && typeof value === 'object') {
            value = value[fallbackKey];
          } else {
            return key;
          }
        }
        return value || key;
      }
    }
    
    return value || key;
  }, [language]);

  const formatCurrency = useCallback((amount, currencyCode = currency) => {
    const currencyConfig = currencies.find(c => c.code === currencyCode);
    if (!currencyConfig) return `${amount} ${currencyCode}`;
    
    return new Intl.NumberFormat(language, {
      style: 'currency',
      currency: currencyCode,
    }).format(amount);
  }, [currency, language]);

  const formatDate = useCallback((dateString) => {
    return new Intl.DateTimeFormat(language, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(new Date(dateString));
  }, [language]);

  return (
    <LanguageContext.Provider
      value={{
        language,
        setLanguage,
        currency,
        setCurrency,
        isRTL,
        t,
        formatCurrency,
        formatDate,
        languages,
        currencies,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
